// src/pages/PreviewDeckPage.tsx
// Vitrine pública — página de um deck com árvore de subdecks
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Tag, FileText, BookOpen, ChevronDown, ChevronRight, ArrowLeft, ScrollText } from 'lucide-react';
import './PreviewDeckPage.css';

interface PreviewCard { front: string }

interface ApostilaPreview { title: string; html: string }

interface SubdeckNode {
    subdeckId: string;
    subdeckName: string;
    subdeckSlug: string;
    cardCount: number;
    apostilaCount: number;
    type: string;
    previewCards: PreviewCard[];
    apostilaPreview: ApostilaPreview | null;
}

interface GroupNode {
    groupId: string;
    groupName: string;
    groupColor: string;
    totalCards: number;
    subdecks: SubdeckNode[];
}

interface DeckDetail {
    deckId: string;
    deckName: string;
    deckDescription: string;
    iconUrl: string | null;
    publicSlug: string;
    totalSubdecks: number;
    totalCards: number;
    groups: GroupNode[];
    ungroupedSubdecks: SubdeckNode[];
}

async function fetchDeckPreview(slug: string): Promise<DeckDetail> {
    const res = await fetch(`/api/public/preview/${slug}`);
    if (!res.ok) throw new Error('Deck não encontrado');
    return res.json();
}

const FlipCard: React.FC<{ front: string }> = ({ front }) => (
    <div className="cpvd-flip-card">
        <div
            className="cpvd-flip-front"
            dangerouslySetInnerHTML={{ __html: front }}
        />
        <div className="cpvd-flip-back">
            <span className="cpvd-blur-pill">🔒 Verso disponível na plataforma</span>
        </div>
    </div>
);

interface SubdeckRowProps {
    sd: SubdeckNode;
    deckSlug: string;
    refParam?: string | null;
    groupColor?: string;
}

const SubdeckRow: React.FC<SubdeckRowProps> = ({ sd, deckSlug, refParam, groupColor }) => {
    const [open, setOpen] = useState(false);
    const isApostila = sd.type === 'apostila';
    const accentColor = groupColor || '#7c3aed';

    // Apostila: linha estática sem expand
    if (isApostila) {
        return (
            <div className="cpvd-subdeck apostila">
                <div
                    className="cpvd-subdeck-header"
                    style={{ borderLeft: `3px solid ${accentColor}`, background: `${accentColor}08` }}
                >
                    <div className="cpvd-subdeck-left">
                        <ScrollText size={15} style={{ color: accentColor, flexShrink: 0 }} />
                        <span className="cpvd-subdeck-name">{sd.subdeckName}</span>
                    </div>
                    <div className="cpvd-subdeck-right">
                        <span
                            className="cpvd-meta-pill apostila"
                            style={{ background: `${accentColor}18`, color: accentColor }}
                        >
                            Apostila
                        </span>
                        <Link
                            to={`/preview/${deckSlug}/${sd.subdeckSlug}${refParam ? `?ref=${refParam}` : ''}`}
                            className="cpvd-subdeck-link"
                            style={{ color: accentColor, borderColor: `${accentColor}40` }}
                        >
                            Ver detalhes
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Flashcard: expansível com preview de cards
    return (
        <div className="cpvd-subdeck">
            <div className="cpvd-subdeck-header" onClick={() => setOpen(o => !o)}>
                <div className="cpvd-subdeck-left">
                    {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <FileText size={15} />
                    <span className="cpvd-subdeck-name">{sd.subdeckName}</span>
                </div>
                <div className="cpvd-subdeck-right">
                    <span className="cpvd-meta-pill">{sd.cardCount} cards</span>
                    <Link
                        to={`/preview/${deckSlug}/${sd.subdeckSlug}${refParam ? `?ref=${refParam}` : ''}`}
                        className="cpvd-subdeck-link"
                        onClick={e => e.stopPropagation()}
                    >
                        Ver detalhes
                    </Link>
                </div>
            </div>

            {open && sd.previewCards.length > 0 && (
                <div className="cpvd-preview-cards">
                    {sd.previewCards.map((c, i) => (
                        <FlipCard key={i} front={c.front} />
                    ))}
                    <div className="cpvd-more-cta">
                        <span>+{sd.cardCount - sd.previewCards.length} cards no total</span>
                        <a href="/cadastro" className="cpvd-cta-sm">Acessar tudo</a>
                    </div>
                </div>
            )}
        </div>
    );
};

const PreviewDeckPage: React.FC = () => {
    const { deckSlug } = useParams<{ deckSlug: string }>();
    const [searchParams] = useSearchParams();
    const ref = searchParams.get('ref');

    const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());

    const { data: deck, isLoading, error } = useQuery({
        queryKey: ['public-preview-deck', deckSlug],
        queryFn: () => fetchDeckPreview(deckSlug!),
        staleTime: 5 * 60 * 1000,
        enabled: !!deckSlug,
    });

    const toggleGroup = (id: string) => {
        setOpenGroups(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    if (isLoading) return <div className="cpv-loading-full">Carregando…</div>;
    if (error || !deck) return (
        <div className="cpv-error-full">
            <p>Módulo não encontrado.</p>
            <Link to="/preview">← Voltar à vitrine</Link>
        </div>
    );

    const totalGroups = deck.groups.length;
    const hasUngrouped = deck.ungroupedSubdecks.length > 0;

    return (
        <div className="cpvd-root" data-theme="light">
            {/* Header */}
            <header className="cpvd-header">
                <div className="cpvd-header-inner">
                    <Link to={`/preview${ref ? `?ref=${ref}` : ''}`} className="cpvd-back">
                        <ArrowLeft size={16} /> Vitrine
                    </Link>
                    <a href="/" className="cpvd-logo">GRAIA</a>
                    <a href="/cadastro" className="cpvd-cta-header">Criar conta grátis</a>
                </div>
            </header>

            <div className="cpvd-layout">
                {/* Main */}
                <main className="cpvd-main">
                    {/* Deck hero */}
                    <div className="cpvd-deck-hero">
                        {deck.iconUrl
                            ? <img src={deck.iconUrl} alt="" className="cpvd-deck-icon" />
                            : <div className="cpvd-deck-icon-placeholder"><BookOpen size={32} /></div>
                        }
                        <div>
                            <h1 className="cpvd-deck-title">{deck.deckName}</h1>
                            {deck.deckDescription && (
                                <p className="cpvd-deck-desc">{deck.deckDescription}</p>
                            )}
                        </div>
                    </div>

                    {/* Progress bar */}
                    <div className="cpvd-progress-bar-wrap">
                        <div className="cpvd-progress-labels">
                            <span>{deck.totalSubdecks} submódulos</span>
                            <span>{deck.totalCards.toLocaleString('pt-BR')} flashcards</span>
                        </div>
                        <div className="cpvd-progress-bar">
                            <div className="cpvd-progress-fill" style={{ width: '100%' }} />
                        </div>
                    </div>

                    {/* Subdecks */}
                    <div className="cpvd-content">
                        {deck.groups.map((group: GroupNode) => (
                            <div key={group.groupId} className="cpvd-group">
                                <div
                                    className="cpvd-group-header"
                                    onClick={() => toggleGroup(group.groupId)}
                                    style={{ borderLeftColor: group.groupColor }}
                                >
                                    <Tag size={15} style={{ color: group.groupColor }} />
                                    <span className="cpvd-group-name">{group.groupName}</span>
                                    <span className="cpvd-group-count">{group.totalCards} cards</span>
                                    {openGroups.has(group.groupId)
                                        ? <ChevronDown size={16} />
                                        : <ChevronRight size={16} />
                                    }
                                </div>
                                {openGroups.has(group.groupId) && (
                                    <div className="cpvd-group-subdecks">
                                        {group.subdecks.map((sd: SubdeckNode) => (
                                            <SubdeckRow
                                                key={sd.subdeckId}
                                                sd={sd}
                                                deckSlug={deck.publicSlug}
                                                refParam={ref}
                                                groupColor={group.groupColor}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}

                        {hasUngrouped && (
                            <div className="cpvd-ungrouped">
                                {deck.ungroupedSubdecks.map((sd: SubdeckNode) => (
                                    <SubdeckRow
                                        key={sd.subdeckId}
                                        sd={sd}
                                        deckSlug={deck.publicSlug}
                                        refParam={ref}
                                    />
                                ))}
                            </div>
                        )}

                        {totalGroups === 0 && !hasUngrouped && (
                            <p className="cpvd-empty">Nenhum submódulo disponível para visualização.</p>
                        )}
                    </div>
                </main>

                {/* Sidebar */}
                <aside className="cpvd-sidebar">
                    <div className="cpvd-sidebar-card">
                        <h3>Acesso completo</h3>
                        <ul className="cpvd-benefits">
                            <li>✓ {deck.totalSubdecks} submódulos completos</li>
                            <li>✓ {deck.totalCards.toLocaleString('pt-BR')} flashcards com verso</li>
                            <li>✓ Apostilas e resumos</li>
                            <li>✓ Repetição espaçada inteligente</li>
                            <li>✓ Estatísticas de desempenho</li>
                        </ul>
                        <a href="/cadastro" className="cpvd-cta-primary">
                            Quero acesso completo
                        </a>
                        <a href="/login" className="cpvd-cta-link">Já tenho conta →</a>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default PreviewDeckPage;
